import { useState } from 'react';
import { format } from 'date-fns';
import { Loader2, Plus, Activity, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { useCandidateActivitiesV2, useAddCandidateActivityV2 } from '@/hooks/useCandidatesV2';

const ACTIVITY_TYPES = ['call', 'email', 'interview', 'note', 'task', 'meeting', 'other'];
const TYPE_ICONS = { call: '📞', email: '✉️', interview: '🎤', note: '📝', task: '✅', meeting: '📅', other: '📌' };

const STATUS_COLORS = {
  pending: 'bg-yellow-100 text-yellow-700',
  completed: 'bg-green-100 text-green-700',
  cancelled: 'bg-gray-100 text-gray-600',
};

const EMPTY_FORM = { type: 'call', title: '', description: '', dueDate: '', status: 'pending' };

const CandidateActivitiesTabV2 = ({ candidateId }) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [titleError, setTitleError] = useState('');

  const { data, isLoading } = useCandidateActivitiesV2(candidateId);
  const addMutation = useAddCandidateActivityV2();

  const activities = data?.activities || data || [];

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.title.trim()) { setTitleError('Title is required'); return; }
    setTitleError('');
    addMutation.mutate(
      { id: candidateId, data: { ...form, title: form.title.trim(), description: form.description.trim() || undefined } },
      { onSuccess: () => { setForm(EMPTY_FORM); setModalOpen(false); } }
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-10 text-gray-400">
        <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading activities...
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <Button size="sm" className="h-8 text-xs gap-1" onClick={() => setModalOpen(true)}>
          <Plus className="h-3 w-3" /> Add Activity
        </Button>
      </div>

      {activities.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-10 text-gray-400">
          <Activity className="h-8 w-8 mb-2 opacity-40" />
          <p className="text-sm">No activities yet</p>
        </div>
      ) : (
        <div className="space-y-3">
          {activities.map((act) => (
            <div key={act._id} className="flex gap-3 text-sm">
              <div className="flex flex-col items-center">
                <div className="w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center text-base flex-shrink-0">
                  {TYPE_ICONS[act.type] || '📌'}
                </div>
                <div className="w-px flex-1 bg-gray-200 mt-1" />
              </div>
              <div className="pb-3 flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-gray-800">{act.title}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[act.status] || 'bg-gray-100 text-gray-600'}`}>
                    {act.status}
                  </span>
                </div>
                {act.description && <p className="text-gray-500 mt-0.5 text-xs">{act.description}</p>}
                <div className="flex items-center gap-3 mt-1 text-xs text-gray-400">
                  {act.dueDate && <span>Due: {format(new Date(act.dueDate), 'MMM dd, yyyy')}</span>}
                  {act.performedBy && <span>by {act.performedBy.name || act.performedBy.email || 'Unknown'}</span>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add Activity Modal */}
      <Dialog open={modalOpen} onOpenChange={(o) => !o && setModalOpen(false)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Activity</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-3">
            <div className="space-y-1">
              <Label>Type</Label>
              <Select value={form.type} onValueChange={(v) => set('type', v)}>
                <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ACTIVITY_TYPES.map((t) => (
                    <SelectItem key={t} value={t}>{TYPE_ICONS[t]} {t.charAt(0).toUpperCase() + t.slice(1)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Title <span className="text-red-500">*</span></Label>
              <Input value={form.title} onChange={(e) => { set('title', e.target.value); setTitleError(''); }} placeholder="Activity title" />
              {titleError && <p className="text-xs text-red-500">{titleError}</p>}
            </div>
            <div className="space-y-1">
              <Label>Description</Label>
              <Textarea value={form.description} onChange={(e) => set('description', e.target.value)} rows={2} placeholder="Optional details..." />
            </div>
            <div className="space-y-1">
              <Label>Due Date</Label>
              <Input type="date" value={form.dueDate} onChange={(e) => set('dueDate', e.target.value)} className="h-8 text-sm" />
            </div>
            <div className="space-y-1">
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => set('status', v)}>
                <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" size="sm" onClick={() => setModalOpen(false)}>Cancel</Button>
              <Button type="submit" size="sm" disabled={addMutation.isPending}>
                {addMutation.isPending && <Loader2 className="mr-2 h-3 w-3 animate-spin" />}
                Add
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CandidateActivitiesTabV2;
